import * as swcHelpers from '@swc/helpers';
import { isNumber } from './type-guards.js';

const recoverableStatusCodes = new Set([
    500,
    502,
    503,
    429
]);
const shouldRetry = (response)=>{
    if (response.ok) {
        return false;
    }
    // Note, we could add support for https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Retry-After
    // But support seems minimal as of time of writing
    return recoverableStatusCodes.has(response.status);
};
const httpMethodsWeCanRetry = new Set([
    'GET',
    'PUT',
    'HEAD',
    'DELETE',
    'OPTIONS',
    'TRACE'
]);
const canRetry = (request)=>{
    var ref;
    // Whitelist some HTTP methods
    if (!httpMethodsWeCanRetry.has((ref = request === null || request === void 0 ? void 0 : request.method) !== null && ref !== void 0 ? ref : 'GET')) {
        return false;
    }
    return true;
};
const pause = (time)=>new Promise((resolve)=>{
        setTimeout(()=>resolve()
        , time);
    })
;
const fetchWithTimeout = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(timeout, ...fetchArgs) {
        const controller = new AbortController();
        const id = setTimeout(()=>{
            controller.abort();
        }, timeout);
        const [requestInfo, requestInit = {}] = fetchArgs;
        const response = yield fetch(requestInfo, swcHelpers.objectSpread({}, requestInit, {
            signal: controller.signal
        }));
        clearTimeout(id);
        return response;
    });
    return function fetchWithTimeout(timeout) {
        return _ref.apply(this, arguments);
    };
}();
const defaultOptions = {
    attempts: [
        // First attempt, allow 10s to respond
        {
            timeout: 10000
        },
        // Wait 2s before trying again, waiting 10s again
        {
            wait: 2000,
            timeout: 10000
        },
        // Wait 5s more seconds for third attempt, leave just 3s for server to respond
        {
            wait: 5000,
            timeout: 3000
        }, 
    ]
};
const resilientFetch = (options)=>{
    const { attempts  } = swcHelpers.objectSpread({}, defaultOptions, options);
    return function() {
        var _ref = swcHelpers.asyncToGenerator(function*(info, init) {
            const url = typeof info === 'string' ? info : info.url;
            // Short-circuit when we can't retry this type of request
            if (!canRetry(init)) {
                return fetch(info, init);
            }
            for(let a = 0; a < attempts.length; a++){
                const attempt = attempts[a];
                // Check if we have to wait before starting attempt
                if (isNumber(attempt.wait)) {
                    console.info(`Retrying ${url} in ${attempt.wait}ms`);
                    yield pause(attempt.wait);
                }
                let response = null;
                try {
                    response = yield fetchWithTimeout(attempt.timeout, info, init);
                } catch (e) {
                    let message = String(e);
                    // Detect local timeout
                    if (e instanceof Error && e.name === 'AbortError') {
                        message = `request took longer than ${attempt.timeout}ms`;
                    }
                    // Log attempt failure except the last attempt.
                    // We'll leave handling the last attempt outside of the loop
                    if (a < attempts.length - 1) {
                        console.warn(`Attempt #${a + 1} to fetch ${url} failed: `, message);
                    }
                }
                if (!response || shouldRetry(response)) {
                    continue;
                }
                return response;
            }
            throw new Error(`Failed to fetch '${url}' after ${attempts.length} attempts`);
        });
        return function(info, init) {
            return _ref.apply(this, arguments);
        };
    }();
};

export { resilientFetch };
//# sourceMappingURL=resilient-fetch.js.map
